/**
 * sysops.mypcb.com page list for the shared panel shell (redesign 0.15.0, Bill 2026-11-03): one
 * entry per page the home tiles linked to, in the sections the routes already use (Kill List,
 * Incidents, Identity, Audit) plus Tools for the OFAC rubric, which shows only while
 * dev.tools.enabled is on (as its tile did). The whole panel is gated by RoleGate (SystemOps or
 * SuperAdmin); there are no per-page roles and no platforms, so no switcher.
 */

import type { ShellNav, ShellPage, ShellTask } from '@retail-ops/panel-shell';

const PAGES: ReadonlyArray<ShellPage & { devOnly?: boolean }> = [
  {
    path: '/kill-list',
    label: 'Kill List',
    section: 'operations',
    help: 'Verify and execute cleanup requests for customer numbers (CIFs), accounts and cards. Open an entry to see its details and history.',
    synonyms: ['decommission', 'cleanup', 'verify', 'execute', 'cif'],
  },
  {
    path: '/incidents/pipeline-errors',
    label: 'Pipeline errors',
    section: 'incidents',
    help: 'Applications stuck in a pipeline error. Look at the failing step, then resume or stop.',
    synonyms: ['stuck', 'failed step', 'error'],
  },
  {
    path: '/incidents/notification-failures',
    label: 'Notification failures',
    section: 'incidents',
    help: 'Notifications that failed to send, with the result for each recipient. Retry or escalate.',
    synonyms: ['email failed', 'retry', 'bounce'],
  },
  {
    path: '/incidents/awaiting-signatures-stuck',
    label: 'Awaiting signature, stuck',
    section: 'incidents',
    help: 'Applications waiting on a customer signature for more than 14 days. Resend or stop.',
    synonyms: ['signature', 'resend', 'stuck signing'],
  },
  {
    path: '/identity/missing-officer-code',
    label: 'Missing operator initials',
    section: 'identity',
    help: 'Staff flagged because they have no operator initials. Each Resolve checks the initials you enter against Core Director before saving.',
    synonyms: ['officer code', 'operator number', 'initials'],
  },
  {
    path: '/identity/cd-name-mismatch',
    label: 'Core Director name mismatch',
    section: 'identity',
    help: "Staff whose name in Core Director does not match their name here. Fix it in Core Director, then clear the flag.",
    synonyms: ['display name', 'name wrong'],
  },
  {
    path: '/identity/operator-catalog-issues',
    label: 'Operator catalog issues',
    section: 'identity',
    help: 'Duplicate initials and name, title or network-ID mismatches between the SharePoint operator list and Entra. Fix them in SharePoint; they clear on the next sync.',
    synonyms: ['duplicate initials', 'operator list'],
  },
  {
    path: '/identity/insider-scan',
    label: 'Insider scan',
    section: 'identity',
    help: "Whether customer contact changes are checked against employees' own customer records, when the employee list was last built, and which employees it cannot cover.",
    synonyms: ['employee', 'insider', 'contact change'],
  },
  {
    path: '/audit/purge-history',
    label: 'Purge history',
    section: 'audit',
    help: 'Every purge of records, read-only.',
    synonyms: ['deleted', 'records management'],
  },
  {
    path: '/audit/test-mode-activity',
    label: 'Test mode activity',
    section: 'audit',
    help: 'The test mode session log. Filter by date, person or session.',
    synonyms: ['test mode', 'sessions'],
  },
  {
    path: '/audit/notifications-history',
    label: 'Notifications history',
    section: 'audit',
    help: 'Every notification sent, searchable, with a CSV export.',
    synonyms: ['emails sent', 'export'],
  },
  {
    path: '/data-mgmt/ofac-rubric',
    label: 'OFAC rubric (developer)',
    section: 'tools',
    devOnly: true,
    help: 'A developer check of the OFAC screening against a list of test names. Shown because developer tools are switched on.',
    synonyms: ['ofac', 'sanctions test'],
  },
];

const SECTIONS: ReadonlyArray<{ id: string; label: string; holds: string }> = [
  { id: 'operations', label: 'Operations', holds: 'Requests waiting for SysOps to act.' },
  { id: 'incidents', label: 'Incidents', holds: 'Applications and notifications that went wrong.' },
  { id: 'identity', label: 'Identity', holds: 'Staff records that need fixing.' },
  { id: 'audit', label: 'Audit', holds: 'Read-only records of cleanup, test mode and notifications.' },
  { id: 'tools', label: 'Tools', holds: 'Developer diagnostics.' },
];

export const SYSOPS_HOME_HELP =
  'Operational health for the Retail Operations Platform: verify and execute kill-list requests, look into pipeline errors and failed notifications, fix staff records, and read the audit logs.';

export function sysopsNav(devToolsEnabled: boolean): ShellNav {
  return {
    root: { label: 'SysOps', path: '/' },
    homeHelp: SYSOPS_HOME_HELP,
    sections: SECTIONS.map((s) => ({
      id: s.id,
      label: s.label,
      holds: s.holds,
      pages: PAGES.filter((pg) => pg.section === s.id && (!pg.devOnly || devToolsEnabled)).map(({ devOnly: _d, ...pg }) => pg),
    })).filter((s) => s.pages.length > 0),
  };
}

export const SYSOPS_TASKS: ReadonlyArray<ShellTask> = [
  {
    id: 'kill',
    title: 'Verify or execute a kill-list request',
    blurb: 'Cleanup requests for customer numbers, accounts and cards.',
    path: '/kill-list',
    icon: 'list',
    crumb: 'Operations › Kill List',
  },
  {
    id: 'notif',
    title: 'Retry a failed notification',
    blurb: 'Failed sends with the result for each recipient.',
    path: '/incidents/notification-failures',
    icon: 'mail',
    crumb: 'Incidents › Notification failures',
  },
  {
    id: 'initials',
    title: 'Fix missing operator initials',
    blurb: 'Each fix is checked against Core Director before it is saved.',
    path: '/identity/missing-officer-code',
    icon: 'user-check',
    crumb: 'Identity › Missing operator initials',
  },
  {
    id: 'insider',
    title: 'Check who the insider scan covers',
    blurb: 'When the employee list was last built and who it cannot cover.',
    path: '/identity/insider-scan',
    icon: 'shield',
    crumb: 'Identity › Insider scan',
  },
  {
    id: 'purge',
    title: 'Look up a purge',
    blurb: 'Every purge of records, read-only.',
    path: '/audit/purge-history',
    icon: 'history',
    crumb: 'Audit › Purge history',
  },
  {
    id: 'notif-history',
    title: 'Find a notification that was sent',
    blurb: 'Search every notification, and export to CSV.',
    path: '/audit/notifications-history',
    icon: 'search',
    crumb: 'Audit › Notifications history',
  },
];
